import { useEffect, useMemo, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { DEFAULT_OPTION, PAGINATION_OPTIONS } from "../../../../utils/Constant";
import SelectBox from "../../../../components/SelectBox";
import { getDistrictsDataAPICall } from "../../../../api/chmActivityAPI";
import { useDispatch, useSelector } from "react-redux";
import { FormUtils, SelectOptionType } from "../../../../utils/FormUtils";
import SearchBox from "../../../../components/SearchBox";
import AddIcon from "@mui/icons-material/Add";
import {
  blocksByDistrictDataAPI,
  districtsByStateDataAPI,
  FilterSurveyorListAPI,
  icvGetStatesDataAPICall,
  icvVendorNamesList,
} from "../../../../api/icvActivityAPI";
import SendIcon from "@mui/icons-material/Send";
import RestartAltTwoToneIcon from "@mui/icons-material/RestartAltTwoTone";
import ModalComponent from "../../../../components/ModalComponent";
import { Controller, useForm } from "react-hook-form";
import { SurveyorCreation } from "./SurveyorCreation";
import { SurveyorFilterListPayload } from "../../../../models/SurveyorModels";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown";
import { handleICVSurveyorPaginationDataSlice } from "../../../../redux/slices/globalSlice";
import {
  handleICVFilteredSurveyorDataSlice,
  handleICVSurveyorDetailsById,
} from "../../../../redux/slices/icvActivitySlice";
import ReusableTable, { Column } from "../../../../components/ReusableTable";
import { Chip } from "@mui/material";
import CPagination from "../../../../components/CPagination";
import { CircleSpinnerOverlay } from "react-spinner-overlay";
import { capitalizeWords } from "../../../../utils/Util";
import DeviceChangeContent from "./DeviceChangeContent";

interface SurveyorSearchProps {}

export const SurveyorSearch = ({}: SurveyorSearchProps) => {
  const { storeICVSurveyorPaginationData } = useSelector(
    (state: any) => state.global,
  );
  const { limit, offset } = storeICVSurveyorPaginationData;
  const {
    storeICVGetStatesData,
    storeICVDistrictsByStateData,
    storeICVBlocksByDistricData,
    storeFilterSurveyorList,
    vendorNamesListData,
    isCrudLoading,
    runListApi,
  } = useSelector((state: any) => state.icvActivity);

  const [surveyorData, setSurveyorData] = useState(storeFilterSurveyorList);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm<SurveyorFilterListPayload>({
    defaultValues: {
      vendorCode: "",
      surveyorName: "",
      surveyorCode: "",
      mobileNumber: "",
      email: "",
      stateIds: [],
      districtIds: [],
      blockIds: [],
      isActive: { label: "Active", value: true },
    },
    mode: "all",
  });
  const selectedState = watch("stateIds");
  const selectedDistrict = watch("districtIds");
  const selectedBlock = watch("blockIds");
  const [openModal, setOpenModal] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitFormHandler, setSubmitFormHandler] = useState<() => void>(
    () => {},
  );
  const [mode, setMode] = useState("add");
  const [surveyorCode, setSurveyorCode] = useState("");
  const vendorNameOptions = FormUtils.convertVendorData(
    vendorNamesListData?.data ?? [],
  );
  const [openActionModal, setOpenActionModal] = useState(false);

  const statusDropdownOptions = [
    { label: "Active", value: 1 },
    { label: "Inactive", value: 0 },
  ];

  const dispatch: any = useDispatch();

  const statesData = FormUtils.convertDataList(
    storeICVGetStatesData?.data ?? [],
  );

  // Multi States, Districts and village - Vendor Allocation
  const multiDistrictsData = FormUtils.convertMultiDataDistrictList(
    storeICVDistrictsByStateData?.data ?? [],
  );

  const multiBlocksData = FormUtils.convertMultiBlocksList(
    storeICVBlocksByDistricData?.data ?? [],
  );

  // Effects
  useEffect(() => {
    // Fetching the states initial data on component mount
    dispatch(icvGetStatesDataAPICall());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      handleReset();
    };
  }, []);

  useEffect(() => {
    if (runListApi) {
      handleSubmit(onSubmit)();
    }
  }, [runListApi]);

  useEffect(() => {
    dispatch(icvVendorNamesList());
  }, []);

  // Restricting the Districts  when state is removed from the state dropdown
  useEffect(() => {
    if (!multiDistrictsData?.length) return;

    const filteredDistricts: any = selectedDistrict.filter((district: any) => {
      return selectedState.some(
        (state: any) => state.value === district.stateId,
      );
    });

    setValue("districtIds", filteredDistricts);
  }, [selectedState]);

  // Restricting the Tehsil/Blocks when district is removed from district dropdown
  useEffect(() => {
    if (!multiBlocksData?.length) return;

    const filteredBlocks: any = selectedBlock.filter((block: any) => {
      return selectedDistrict.some(
        (district: any) => district.value === block.districtId,
      );
    });

    setValue("blockIds", filteredBlocks);
  }, [selectedDistrict]);

  // Handlers
  const onchangeDropdown = (label: String, event: any) => {
    switch (label) {
      case "State":
        const stateCodes: string[] = Array.from(
          new Set(event.map((item: SelectOptionType) => item.value)),
        );
        dispatch(districtsByStateDataAPI(stateCodes));

        break;
      case "District":
        const stateCodesfromDistricts: string[] = Array.from(
          new Set(event.map((item: any) => item.stateId)),
        );

        const districtCodes: string[] = Array.from(
          new Set(event.map((item: any) => item.value)),
        );

        dispatch(
          blocksByDistrictDataAPI(stateCodesfromDistricts, districtCodes),
        );

        break;

      default:
        dispatch(getDistrictsDataAPICall(event.value));
        break;
    }
  };

  const getUniqueValues = (arr: any[], key: string) => {
    return Array.from(
      new Set(
        arr?.map((a: any) => capitalizeWords(a[key])).filter((val: any) => val),
      ),
    ).join(", ");
  };

  const Surveyor_Columns: Column<any>[] = [
    {
      header: "Surveyor ID",
      accessor: "surveyorCode",
      sticky: "left",
      className: "view-form highlight-background",
    },

    {
      header: "Vendor Name",
      accessor: "vendorName",
      cell: (row) => row.vendorName ?? "-",
      tooltip: (row) => row.vendorName,
    },

    {
      header: "Surveyor Name",
      accessor: "surveyorName",
      tooltip: (row) => row.surveyorName,
    },

    {
      header: "Email ID",
      accessor: "emailId",
      cell: (row) => row.emailId ?? "-",
      tooltip: (row) => row.emailId,
    },

    {
      header: "Mobile Number",
      accessor: "mobileNumber",
    },

    {
      header: "Status",
      accessor: "isActive",

      cell: (row) => (
        <Chip
          label={row.isActive === true ? "Active" : "Inactive"}
          size="small"
          id={row.isActive === true ? "status-2" : "status-1"}
        />
      ),
    },

    {
      header: "Allocated State",
      className: "reusable-status-cell",
      tooltip: (row) =>
        getUniqueValues(row?.allocations || [], "stateName") || "-",
      cell: (row) =>
        getUniqueValues(row?.allocations || [], "stateName") || "-",
    },
    {
      header: "Allocated District",
      tooltip: (row) =>
        getUniqueValues(row?.allocations || [], "districtName") || "-",
      cell: (row) =>
        getUniqueValues(row?.allocations || [], "districtName") || "-",
    },
    {
      header: "Allocated Tehsil/Block",
      tooltip: (row) =>
        getUniqueValues(row?.allocations || [], "tehsilBlockName") || "-",
      cell: (row) =>
        getUniqueValues(row?.allocations || [], "tehsilBlockName") || "-",
    },
    // {
    //   header: "Action",
    //   accessor: "action",
    //   cell: (row) => (
    //     <Button
    //       variant="contained"
    //       // className="my-1 btn btn-sm cl-btn-outline-primary"
    //       className="my-1 btn btn-sm custom-btn-outline-primary"
    //       onClick={(e) => {
    //         e.stopPropagation();
    //         setOpenActionModal(true);
    //       }}
    //     >
    //       Action
    //     </Button>
    //   ),
    // },
  ];

  const toggleOpenModal = () => {
    setOpenModal(!openModal);
    setMode("");
    setSurveyorCode("");
  };

  const toggleOpenActionModal = () => {
    setOpenActionModal(!openActionModal);
    setSurveyorCode("");
  };
  const handleReset = () => {
    reset();
    dispatch(
      handleICVFilteredSurveyorDataSlice({
        data: [],
        count: 0,
      }),
    );
  };

  const onSubmit = (
    data: SurveyorFilterListPayload,
    customLimit?: any,
    customOffset?: any,
  ) => {
    const finalLimit = customLimit ?? limit;
    const finalOffset = customOffset ?? offset;

    dispatch(FilterSurveyorListAPI(data, finalLimit, finalOffset));
  };

  // Slice the data based on the pagination offset and limit
  const paginatedData = useMemo(() => {
    setSurveyorData(storeFilterSurveyorList);
    let filterData = storeFilterSurveyorList?.data;
    if (filterData) {
      return filterData;
    }
    return [];
  }, [storeFilterSurveyorList, storeICVSurveyorPaginationData]);

  const changePaginationLimit = (value: number) => {
    if (surveyorData?.data?.length > 0) {
      dispatch(
        handleICVSurveyorPaginationDataSlice({
          ...storeICVSurveyorPaginationData,
          offset: 0,
          limit: value,
          selectedLimit: value,
        }),
      );
      handleSubmit((data) => onSubmit(data, value, 0))();
    }
  };

  const prevPage = () => {
    const newOffset =
      storeICVSurveyorPaginationData.offset -
      storeICVSurveyorPaginationData.selectedLimit;
    if (newOffset >= 0) {
      const newLimit = storeICVSurveyorPaginationData.selectedLimit;

      dispatch(
        handleICVSurveyorPaginationDataSlice({
          ...storeICVSurveyorPaginationData,
          offset: newOffset,
          limit: newLimit,
        }),
      );

      handleSubmit((data) => onSubmit(data, newLimit, newOffset))();
    }
  };

  const nextPage = () => {
    const count = storeICVSurveyorPaginationData.selectedLimit;
    if (surveyorData.count > 0 && surveyorData.count === count) {
      dispatch(
        handleICVSurveyorPaginationDataSlice({
          ...storeICVSurveyorPaginationData,
          offset:
            storeICVSurveyorPaginationData.offset +
            storeICVSurveyorPaginationData.selectedLimit,
          limit: storeICVSurveyorPaginationData.selectedLimit,
        }),
      );
      handleSubmit((data) =>
        onSubmit(
          data,
          storeICVSurveyorPaginationData.selectedLimit,
          storeICVSurveyorPaginationData.offset +
            storeICVSurveyorPaginationData.selectedLimit,
        ),
      )();
    }
  };

  return (
    <>
      <>
        {isCrudLoading && (
          <CircleSpinnerOverlay
            loading={isCrudLoading}
            overlayColor="rgba(147, 166, 199, 0.1)"
            color="#5C79AA"
            zIndex={9999}
          />
        )}
        <div className="filter-section mb-2">
          <Row>
            {/* Vendor Name */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendorCode"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SelectBox
                    {...field}
                    label="Vendor Name"
                    options={vendorNameOptions}
                    className="css-select-bg"
                  />
                )}
              />
            </Col>

            {/* Surveyor Name */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="surveyorName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Surveyor Name"
                      placeHolder="Enter Surveyor Name"
                      maxLength={255}
                      error={!!error}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Surveyor Code */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="surveyorCode"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Surveyor ID"
                      placeHolder="Enter Surveyor ID"
                      maxLength={255}
                      error={!!error}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Mobile Number */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="mobileNumber"
                control={control}
                rules={{
                  pattern: {
                    value: /^[6-9][0-9]{9}$/,
                    message:
                      "Enter a valid 10‑digit mobile number starting with 6–9",
                  },
                  minLength: {
                    value: 10,
                    message: "mobile number must be 10 digits",
                  },
                  maxLength: {
                    value: 10,
                    message: "mobile number must be 10 digits",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Mobile Number"
                      placeHolder="Enter Mobile Number"
                      error={!!error}
                      maxLength={10}
                      errorMessage={error?.message}
                      onChange={(e: any) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);

                        field.onChange(value);
                      }}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Surveyor Email */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Email"
                      placeHolder="Enter Email"
                      error={!!error}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Status Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="isActive"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SelectBox
                    {...field}
                    label="Status"
                    options={statusDropdownOptions}
                    className="css-select-bg"
                  />
                )}
              />
            </Col>

            {/* Multi Selection State Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="stateIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    fromVendorCreation
                    label="State"
                    options={statesData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("stateIds", event, {
                          shouldValidate: true,
                        });

                        onchangeDropdown("State", event);
                      } else {
                        setValue("stateIds", [DEFAULT_OPTION]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* Multi Selection District Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="districtIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    fromVendorCreation
                    disabled={Number(selectedState.length) === 0}
                    label="District"
                    options={multiDistrictsData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("districtIds", event, {
                          shouldValidate: true,
                        });
                        onchangeDropdown("District", event);
                      } else {
                        setValue("districtIds", [DEFAULT_OPTION]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* Tehsil/Block Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="blockIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    fromVendorCreation
                    disabled={Number(selectedDistrict.length) === 0}
                    label="Tehsil/Block"
                    options={multiBlocksData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("blockIds", event, {
                          shouldValidate: true,
                        });
                      } else {
                        setValue("blockIds", [DEFAULT_OPTION]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* Add Surveyor Button */}
            <Col xs={12} md={4} lg={3}>
              <div className="mt-4" style={{ position: "relative" }}>
                <div className="mb-1 text-start search-label text-muted"></div>
                <Button
                  variant="contained"
                  className="w-100 d-flex justify-content-center align-items-center cl-btn-outline-primary"
                  onClick={() => {
                    setOpenModal(true);
                  }}
                >
                  <AddIcon sx={{ fontSize: "18px", marginRight: "5px" }} />
                  Add Surveyor
                </Button>
              </div>
            </Col>

            {/* action buttons */}
            <Col xs={12} md={4} lg={3}>
              <div className="mt-4" style={{ position: "relative" }}>
                <div className="mb-1 text-start search-label text-muted"></div>
                <div className="d-flex gap-2">
                  <Button
                    variant="contained"
                    className="w-100 cl-btn-outline-primary d-flex align-items-center justify-content-center"
                    onClick={handleReset}
                  >
                    Reset
                    <RestartAltTwoToneIcon sx={{ ml: 1 }} />
                  </Button>

                  <Button
                    variant="contained"
                    className="w-100 cl-btn-primary d-flex align-items-center justify-content-center"
                    onClick={handleSubmit((data) => onSubmit(data, 25, 0))}
                  >
                    Submit
                    <SendIcon sx={{ fontSize: "18px", ml: 1 }} />
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div style={{ marginTop: 20, overflowX: "hidden", maxWidth: "100%" }}>
          {paginatedData.length > 0 ? (
            <ReusableTable
              columns={Surveyor_Columns}
              data={paginatedData}
              rowKey={(row: any) => row.vendorId}
              onRowClick={(row: any) => {
                // Handle row click if needed
                setOpenModal(true);
                setSurveyorCode(row.surveyorCode);
                setMode("edit");
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "60px 20px",
                margin: "20px 0",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                  Please apply appropriate filters and submit to view the
                  surveyors
                </div>
              </div>
            </div>
          )}
        </div>
        <CPagination
          storeCHMDashboardData={surveyorData}
          storeCHMPaginationData={storeICVSurveyorPaginationData}
          paginationLimitOptions={PAGINATION_OPTIONS.DEFAULT}
          changePaginationLimit={changePaginationLimit}
          nextPage={nextPage}
          prevPage={prevPage}
          showTotalCount={false}
          showHelper={true}
          helperText={`Use the 'Next(≫)' and 'Previous(≪)' buttons to browse through more data.`}
        />
      </>
      {openModal && (
        <ModalComponent
          openModal={openModal}
          toggleModal={toggleOpenModal}
          title={mode === "edit" ? "Edit Surveyor" : "Create Surveyor"}
          children={
            <SurveyorCreation
              setIsFormValid={setIsFormValid}
              setSubmitFormHandler={setSubmitFormHandler}
              setOpenModal={setOpenModal}
              mode={mode}
              surveyorCode={surveyorCode}
            />
          }
          needXlSize
          footer={
            <div className="d-flex justify-content-end">
              <Button
                variant="outline-primary"
                className="mx-2 cl-btn-outline-primary"
                onClick={() => {
                  setOpenModal(false);
                  setMode("");
                  setSurveyorCode("");
                  dispatch(handleICVSurveyorDetailsById([]));
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="mx-2 cl-btn-primary"
                disabled={!isFormValid}
                onClick={() => {
                  submitFormHandler();
                }}
              >
                Submit
              </Button>
            </div>
          }
        />
      )}

      {/* Action Modal */}
      {openActionModal && (
        <ModalComponent
          openModal={openActionModal}
          toggleModal={toggleOpenActionModal}
          title={"Device Change Request Details"}
          size={"md"}
          children={<DeviceChangeContent />}
          footer={
            <div className="d-flex justify-content-end">
              <Button
                variant="outline-primary"
                className="mx-2 cl-btn-outline-primary"
                onClick={() => {
                  setOpenActionModal(false);
                  setSurveyorCode("");
                }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                className="mx-2 cl-btn-primary"
                // disabled={!isFormValid}
                onClick={() => {
                  // submitFormHandler();
                }}
              >
                Approve
              </Button>
            </div>
          }
        />
      )}
    </>
  );
};
